import { IAreaItem, IAreaSerialized } from '@/models/Area/interfaces/IAreaSerialized';
import { EN_CONTENTS_TYPE_CODE, TContentsType } from '@/models/Place/interfaces/IPlaceDetailSerialized';
import { IRegionAccommodationPayload } from '@/models/Region/interfaces/IRegionAccommodationPayload';
import { SEARCH_DOMESTIC_PAGE_NAME, SEARCH_DOMESTIC_TYPE } from '@/models/Search/interfaces/ISearchDomesticCommon';
import type { TSearchDomesticPageName, TSearchDomesticType } from '@/models/Search/interfaces/ISearchDomesticCommon';
import { ISearchListPayload } from '@/models/Search/interfaces/ISearchListPayload';
import { ISearchListQueryParams } from '@/models/Search/interfaces/ISearchListRequestParams';
import { IPolicy } from '@/models/Search/policies/IPolicy';
import {
  EN_CAT_KEY,
  EN_PLACELIST_PATH_DIVISION,
  EN_PLACELIST_TYPE,
  EN_REGION_TYPE,
  ISearchListPolicy,
} from '@/models/Search/policies/ISearchListPolicy';
import * as listPolicy from '@/models/Search/policies/listPolicy';
import SearchListAreaPolicy from '@/models/Search/policies/SearchListAreaPolicy';
import SearchListAroundPolicy from '@/models/Search/policies/SearchListAroundPolicy';
import SearchListKeywordPolicy from '@/models/Search/policies/SearchListKeywordPolicy';
import SearchListOldAreaPolicy from '@/models/Search/policies/SearchListOldAreaPolicy';
import SearchListRecommendPolicy from '@/models/Search/policies/SearchListRecommendPolicy';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import StoreService from '@/services/StoreService';
import { YaMo } from '@/services/YaMoment';
import { roomsQueryToCapaticy } from '@/utils/FilterUtils';
import { checkIsServer } from '@/utils/functions/checkServerEnvironment';
import { IBottomSheetOrderingOption } from '@/utils/types';
import { T, always, cond, equals, find, forEach, omit, test, values } from 'ramda';

// 국내숙소 리스트 저장 키
export const PLACE_LIST_CACHE_KEY = 'place_list_cache';
// 국내숙소 리스트 메타 저장 키
export const PLACE_LIST_CACHE_META_KEY = 'place_list_cache_meta';
// 국내숙소 리스트 저장 소스 키
export const PLACE_LIST_CACHE_FROM_KEY = 'place_list_cache_from';

/**
 * `EN_PLACELIST_TYPE`을 `EN_REGION_TYPE`으로 변환한다.
 * @param placeListType 변환할 `PLACELIST_TYPE`
 * @returns `REGION_TYPE`. `PLACELIST_TYPE.TOPAD`는 `REGION_TYPE.MOTEL`이다.
 */
export const getRegionType = (placeListType: EN_PLACELIST_TYPE): EN_REGION_TYPE => {
  switch (placeListType) {
    case EN_PLACELIST_TYPE.HOTEL:
      return EN_REGION_TYPE.HOTEL;
    case EN_PLACELIST_TYPE.PENSION:
      return EN_REGION_TYPE.PENSION;
    case EN_PLACELIST_TYPE.GUESTHOUSE:
      return EN_REGION_TYPE.GUESTHOUSE;
    default:
      // MOTEL, TOPAD, AROUND, RECOMMEND 외
      return EN_REGION_TYPE.MOTEL;
  }
};

/**
 * `TContentsType`을 통해 PDP url 로 변환한다.
 * @param contentsType
 * @returns EN_PLACELIST_TYPE
 */
export const getPlaceTypePath = (contentsType?: TContentsType): EN_PLACELIST_TYPE | 'places' => {
  switch (Number(contentsType)) {
    case EN_CONTENTS_TYPE_CODE.MOTEL:
      return EN_PLACELIST_TYPE.MOTEL;
    case EN_CONTENTS_TYPE_CODE.HOTEL:
      return EN_PLACELIST_TYPE.HOTEL;
    case EN_CONTENTS_TYPE_CODE.PENSION:
      return EN_PLACELIST_TYPE.PENSION;
    case EN_CONTENTS_TYPE_CODE.GUESTHOUSE:
      return EN_PLACELIST_TYPE.GUESTHOUSE;
    default:
      return 'places';
  }
};

export const getPlaceListName = (placeListType: EN_PLACELIST_TYPE): string => {
  switch (placeListType) {
    case EN_PLACELIST_TYPE.RECOMMEND:
      return 'Coupon';
    case EN_PLACELIST_TYPE.MOTEL:
      return 'Motel';
    case EN_PLACELIST_TYPE.HOTEL:
      return 'Hotel';
    case EN_PLACELIST_TYPE.PENSION:
      return 'Pension';
    case EN_PLACELIST_TYPE.GUESTHOUSE:
      return 'GuestHouse';
    case EN_PLACELIST_TYPE.AREA:
      return 'OldLocal';
    case EN_PLACELIST_TYPE.SEARCH:
      return 'SearchResult';
    default:
      return 'Around';
  }
};

const getType = cond([
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.MKEY}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.MKEY)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.HKEY}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.HKEY)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.PKEY}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.PKEY)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.GKEY}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.GKEY)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.REGION}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.REGION)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.AREA}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.AREA)],
  [test(new RegExp(`${EN_PLACELIST_PATH_DIVISION.KEYWORD}-.+`, 'gi')), always(EN_PLACELIST_PATH_DIVISION.KEYWORD)],
  [T, always(EN_PLACELIST_PATH_DIVISION.KEYWORD)],
]);

export const getPathDivisionType = (pathDivision: string): EN_PLACELIST_PATH_DIVISION => {
  return getType(pathDivision);
};

export const getPathDivision = (placeListType: EN_PLACELIST_TYPE): EN_PLACELIST_PATH_DIVISION => {
  switch (placeListType) {
    case EN_PLACELIST_TYPE.AROUND:
    case EN_PLACELIST_TYPE.AROUND_MOTEL:
      return EN_PLACELIST_PATH_DIVISION.KEYWORD;
    case EN_PLACELIST_TYPE.MOTEL:
      return EN_PLACELIST_PATH_DIVISION.MKEY;
    case EN_PLACELIST_TYPE.HOTEL:
      return EN_PLACELIST_PATH_DIVISION.HKEY;
    case EN_PLACELIST_TYPE.PENSION:
      return EN_PLACELIST_PATH_DIVISION.PKEY;
    case EN_PLACELIST_TYPE.GUESTHOUSE:
      return EN_PLACELIST_PATH_DIVISION.GKEY;
    case EN_PLACELIST_TYPE.AREA:
      return EN_PLACELIST_PATH_DIVISION.AREA;
    default:
      return EN_PLACELIST_PATH_DIVISION.KEYWORD;
  }
};

export const getPathDivisionValue = (placeListType: EN_PLACELIST_TYPE, pathDivision: string): string => {
  const pathDivisionType = getPathDivision(placeListType);
  return pathDivision
    .replace(`${EN_PLACELIST_PATH_DIVISION.REGION}-`, '')
    .replace(`${EN_PLACELIST_PATH_DIVISION.AREA}-`, '')
    .replace(`${pathDivisionType}-`, '')
    .replace(/\//gi, '');
};

export const getPathDivisionCutsValue = (placeListType: EN_PLACELIST_TYPE, pathDivisionCuts: string): string => {
  const pathDivisionCutsType = getPathDivision(placeListType);
  return pathDivisionCuts.replace(`${pathDivisionCutsType}-`, '').replace(/\//gi, '');
};

export const getMatchedPolicy = (
  pathDivisionValue: string,
  pathDivisionCutsValue: string,
  tabs: IPolicy[],
): IPolicy => {
  const isMatchPolicy = (item: IPolicy): boolean => {
    if (!pathDivisionCutsValue) {
      return item.catkey.toString() === pathDivisionValue;
    }
    return item.catkey.toString() === pathDivisionCutsValue;
  };

  return tabs.find(isMatchPolicy) || tabs[0];
};

export const getPolicyByPlaceListType = (placeListType: EN_PLACELIST_TYPE): IPolicy[] => {
  switch (placeListType) {
    case EN_PLACELIST_TYPE.AROUND:
      return listPolicy.oldAround;
    case EN_PLACELIST_TYPE.RECOMMEND:
      return listPolicy.recommend;
    case EN_PLACELIST_TYPE.AROUND_MOTEL:
      return listPolicy.oldAroundMotel;
    case EN_PLACELIST_TYPE.MOTEL:
      return listPolicy.motel;
    case EN_PLACELIST_TYPE.HOTEL:
      return listPolicy.hotel;
    case EN_PLACELIST_TYPE.GUESTHOUSE:
      return listPolicy.guestHouse;
    case EN_PLACELIST_TYPE.PENSION:
      return listPolicy.pension;
    case EN_PLACELIST_TYPE.AREA:
      return listPolicy.oldArea;
    default:
      return listPolicy.oldAround;
  }
};

const getPlaceListAroundPageName = cond([
  [equals(EN_CAT_KEY.RECOMMEND), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__COUPON)],
  [equals(EN_CAT_KEY.MOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__MOTEL)],
  [equals(EN_CAT_KEY.HOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__HOTEL)],
  [equals(EN_CAT_KEY.PENSION), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__PENSION)],
  [equals(EN_CAT_KEY.GUEST_HOUSE), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__GUESTHOUSE)],
  [T, always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND__SHORTCUT)],
]);
const getPlaceListAreaPageName = cond([
  [equals(EN_CAT_KEY.RECOMMEND), always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__RECOMMEND)],
  [equals(EN_CAT_KEY.MOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__MOTEL)],
  [equals(EN_CAT_KEY.HOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__HOTEL)],
  [equals(EN_CAT_KEY.PENSION), always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__PENSION)],
  [equals(EN_CAT_KEY.GUEST_HOUSE), always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__GUESTHOUSE)],
  [T, always(EN_PAGE_NAME.PAGE__PLACELIST__AREA__SHORTCUT)],
]);

export const getPlaceListPageName = (
  placeListType: EN_PLACELIST_TYPE,
  pathDivisionValue: string,
  pathDivisionCutsValue: string,
): { pageName: EN_PAGE_NAME; shortcut: string } => {
  const tabs = getPolicyByPlaceListType(placeListType);
  const activePolicy = getMatchedPolicy(pathDivisionValue, pathDivisionCutsValue, tabs);

  const pageName = cond([
    [equals(EN_PLACELIST_TYPE.AROUND), always(getPlaceListAroundPageName(activePolicy.catkey))],
    [equals(EN_PLACELIST_TYPE.AREA), always(getPlaceListAreaPageName(activePolicy.catkey))],
    [equals(EN_PLACELIST_TYPE.AROUND_MOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__AROUND_MOTEL__SHORTCUT)],
    [equals(EN_PLACELIST_TYPE.MOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__MOTEL__SHORTCUT)],
    [equals(EN_PLACELIST_TYPE.HOTEL), always(EN_PAGE_NAME.PAGE__PLACELIST__HOTEL__SHORTCUT)],
    [equals(EN_PLACELIST_TYPE.PENSION), always(EN_PAGE_NAME.PAGE__PLACELIST__PENSION__SHORTCUT)],
    [equals(EN_PLACELIST_TYPE.GUESTHOUSE), always(EN_PAGE_NAME.PAGE__PLACELIST__GUESTHOUSE__SHORTCUT)],
  ])(placeListType);

  return {
    pageName,
    shortcut: activePolicy.title,
  };
};

export const getAreaName = (code: number, data: IAreaSerialized): string => {
  let locationName = '';
  forEach<IAreaItem>((group) => {
    const result = find<IAreaItem>((item) => equals(Number(code), item.area), group.children);

    if (result) {
      locationName = result.name;
    }
  }, data);
  return locationName;
};

export interface IFilterOptionItem {
  label: string;
  value: string;
  tooltip?: string;
}

/*
 * 정렬기준 및 코드 현황
 * https://yanolja.atlassian.net/wiki/spaces/YASEARCH/pages/72450567
 * 참고
 *  1. 페이지 목적에 따라 같은 용어지만 사용 코드가 달라질 수 있다.
 *  2. APP 한정 '내주변' 지면이 개편되어, APP과 WEB이 다른 정렬코드로 동작하도록 구현됨.
 */

export const sortSearchSubwayOptions = new Map<number, IBottomSheetOrderingOption>([
  [100, { title: '야놀자 추천순' }],
  [132, { title: '예약가 낮은 순' }],
  [131, { title: '예약가 높은 순' }],
  [134, { title: '후기 좋은 순' }],
  [133, { title: '후기 많은 순' }],
  [105, { title: '찜 많은 순' }],
]);
/*
 * 정렬 기준 고시 ver3
 * https://yanolja.atlassian.net/wiki/spaces/PLPSRP/pages/43302609
 */
// SRP
export const sortSearchKeywordOptions = new Map<number, IBottomSheetOrderingOption>([
  [
    100,
    {
      title: '야놀자 추천순',
      notice: {
        description:
          '노출은 상단 광고 영역과 하단 검색결과 영역으로 나뉩니다. 검색결과 영역은 검색 정확도, 판매량, 후기 수를 포함한 사용자 반응을 종합적으로 분석해 정렬합니다.',
      },
    },
  ],
  [132, { title: '예약가 낮은 순' }],
  [131, { title: '예약가 높은 순' }],
  [134, { title: '후기 좋은 순' }],
  [133, { title: '후기 많은 순' }],
  [105, { title: '찜 많은 순' }],
]);
// 지도검색PLP, 내주변PLP
export const sortAroundOptions = new Map<number, IBottomSheetOrderingOption>([
  [
    107,
    {
      title: '야놀자 추천순',
      notice: {
        description:
          '노출은 상단 광고 영역과 하단 비광고 영역으로 나뉩니다. 비광고 영역은 판매량, 후기 수를 포함한 사용자 반응을 종합적으로 분석해 정렬합니다.',
      },
    },
  ],
  [128, { title: '예약가 낮은 순' }],
  [127, { title: '예약가 높은 순' }],
  [134, { title: '후기 좋은 순' }],
  [133, { title: '후기 많은 순' }],
  [105, { title: '찜 많은 순' }],
]);
// 서브홈PLP, 지역PLP, PLP
export const sortAreaOptions = new Map<number, IBottomSheetOrderingOption>([
  [
    106,
    {
      title: '야놀자 추천순',
      notice: {
        description:
          '노출은 상단 광고 영역과 하단 비광고 영역으로 나뉩니다. 비광고 영역은 판매량, 후기 수를 포함한 사용자 반응을 종합적으로 분석해 정렬합니다.',
      },
    },
  ],
  [124, { title: '예약가 낮은 순' }],
  [123, { title: '예약가 높은 순' }],
  [134, { title: '후기 좋은 순' }],
  [133, { title: '후기 많은 순' }],
  [105, { title: '찜 많은 순' }],
]);

export const hotelGradeOptions: IFilterOptionItem[] = [
  { label: '5성급', value: 'grade05' },
  { label: '4성급', value: 'grade04' },
  { label: '3성급', value: 'grade03' },
  { label: '2성급', value: 'grade02' },
  { label: '1성급', value: 'grade01' },
  { label: '호텔', value: 'hotel' },
  { label: '리조트', value: 'resort01' },
  { label: '전통호텔', value: 'tr_hotel' },
  { label: '레지던스', value: 'residence01' },
];

export const pensionPickupOptions: IFilterOptionItem[] = [
  { label: '마트픽업', value: 'pickup_mart' },
  { label: '자체픽업', value: 'pickup_self' },
];

export const priceRangeOptions: IFilterOptionItem[] = [
  { label: '1만원', value: '10000' },
  { label: '2만원', value: '20000' },
  { label: '3만원', value: '30000' },
  { label: '4만원', value: '40000' },
  { label: '5만원', value: '50000' },
  { label: '6만원', value: '60000' },
  { label: '7만원', value: '70000' },
  { label: '8만원', value: '80000' },
  { label: '9만원', value: '90000' },
  { label: '10만원', value: '100000' },
  { label: '15만원', value: '150000' },
  { label: '20만원', value: '200000' },
  { label: '25만원', value: '250000' },
  { label: '30만원', value: '300000' },
  { label: '35만원', value: '350000' },
  { label: '40만원', value: '400000' },
  { label: '45만원', value: '450000' },
  { label: '50만원 이상', value: '500000' },
];

export const quickFilterOptions: IFilterOptionItem[] = [
  { label: '즉시할인', value: 'coupon' },
  { label: '예약가능', value: 'excludeSoldout' },
  { label: '대실/DayUse', value: 'includeRent' },
  { label: '대실', value: 'includeRent' },
  { label: 'DayUse', value: 'includeRent' },
  { label: '숙박', value: 'includeStay' },
];

export type TSavedFilters = Pick<
  ISearchListQueryParams,
  'sort' | 'themes' | 'kto_grade' | 'pickup' | 'bestPick' | 'bestPrice' | 'minPrice' | 'maxPrice'
>;

export const omitFilterKeys = [
  'sort',
  'themes',
  'kto_grade',
  ...hotelGradeOptions.map((_, index) => `kto_grade[${index}]`),
  'pickup',
  ...pensionPickupOptions.map((_, index) => `pickup[${index}]`),
  'minPrice',
  'maxPrice',
  'checkinDate',
  'checkoutDate',
  'addressCode',
];

export const omitUrlParams = ['checkinDate', 'checkoutDate', 'capacityAdult', 'capacityChild', 'capacityChildAges'];

export const getSearchListPolicy = (
  placeListType: EN_PLACELIST_TYPE,
  policy: IPolicy,
  pathDivision: string = '',
): ISearchListPolicy => {
  switch (placeListType) {
    case EN_PLACELIST_TYPE.AROUND:
      if (policy.category === EN_PLACELIST_TYPE.RECOMMEND) {
        return new SearchListRecommendPolicy(policy);
      }
      return new SearchListAroundPolicy(policy);
    case EN_PLACELIST_TYPE.RECOMMEND:
      return new SearchListRecommendPolicy(policy);
    case EN_PLACELIST_TYPE.MOTEL:
    case EN_PLACELIST_TYPE.HOTEL:
    case EN_PLACELIST_TYPE.GUESTHOUSE:
    case EN_PLACELIST_TYPE.PENSION:
      return new SearchListAreaPolicy(policy);
    case EN_PLACELIST_TYPE.KEYWORD:
    case EN_PLACELIST_TYPE.SEARCH:
      if (pathDivision && getPathDivisionType(pathDivision) !== EN_PLACELIST_PATH_DIVISION.KEYWORD) {
        return new SearchListOldAreaPolicy(policy);
      }
      return new SearchListKeywordPolicy(policy);
    case EN_PLACELIST_TYPE.AREA:
      return new SearchListOldAreaPolicy(policy);
    default:
      return new SearchListAroundPolicy(policy);
  }
};

/**
 * [YWEB-4027] 미리예약 180일 이후 접근 방어
 * checkin 이 미리예약 180일 이후일때 를 판별
 * @param 체크인(YYYY-MM-DD)
 * @returns 미리예약 가능 여부
 */
export const isValidCheckinDate = (checkinDate: string): boolean => {
  const limitDay = YaMo.getDateFormat(YaMo.getSeoulTime().subtract(2, 'hours').add(180, 'day'));
  if (checkinDate >= limitDay) {
    return false;
  }

  return true;
};

export const placeQueryRedirect = ({ req }) => {
  /**
   * cIn, cOut, rooms 가 query parameter 로 지정해서 들어올 경우 session 에 저장 후 redirection 을 수행한다
   */
  const { cIn, cOut, rooms } = req.query;

  if (cIn || cOut || rooms) {
    if (cIn && cOut) {
      req.session.setCheckIODates(cIn, cOut);
    }
    if (rooms) {
      req.session.setCapacityInfo(roomsQueryToCapaticy(rooms));
    }

    return omit(['cIn', 'cOut', 'rooms'], req.query);
  }

  return false;
};

/**
 * 지역 리스트 정보에서 지역명을 가져온다.
 *
 * @param code 지역 코드
 * @param region 지역 리스트 정보
 * @param groupName
 * @returns 지역명
 */
export const getRegionName = (code: string, region?: IRegionAccommodationPayload, groupName?: string): string => {
  if (!region) {
    return '';
  }
  groupName = region.depth === 2 ? region.title : groupName;
  if (region.code === code) {
    return region.title === '지역TOP' ? `${groupName} TOP 숙소` : region.title;
  }

  for (const child of region.items) {
    const result = getRegionName(code, child, groupName);
    if (result) {
      return result;
    }
  }
  return '';
};

/**
 * 세션 스토리지에 탭(모텔, 호텔, 펜션...) 별 국내 숙소 필터 정보를 저장한다.
 *
 * @param key 탭 구분 키
 * @param filters 탭 별 필터 정보
 */
export const setPlaceFilters = ({ key, filters }: { key: string; filters: ISearchListQueryParams }): void => {
  StoreService.getInstance('session').set('SavedFiltersKey', key);
  const savedFilters = StoreService.getInstance('session').get('SavedFilters') ?? {};
  StoreService.getInstance('session').set('SavedFilters', {
    ...savedFilters,
    [key]: filters,
  });
};

/**
 * 세션 스토리지에 저장된 국내 숙소 필터 정보를 가져온다.
 *
 * @param key 탭 구분 키
 */
export const getPlaceFilters = (key?: string): { key: string; filters: ISearchListQueryParams } => {
  const savedFiltersKey = key ?? StoreService.getInstance('session').get('SavedFiltersKey') ?? '';
  const savedFilters = StoreService.getInstance('session').get('SavedFilters') ?? {};

  return {
    key: savedFiltersKey,
    filters: savedFilters[savedFiltersKey] ?? {},
  };
};

/**
 * 세션 스토리지에 저장된 국내 숙소 필터 정보를 삭제한다.
 *
 * @param isAll 전체 삭제 여부
 */
export const clearPlaceFilters = (isAll?: boolean): void => {
  if (isAll) {
    StoreService.getInstance('session').remove('SavedFiltersKey');
    StoreService.getInstance('session').remove('SavedFilters');
  } else {
    const currentKey = StoreService.getInstance('session').get('SavedFiltersKey');
    const savedFilters = StoreService.getInstance('session').get('SavedFilters') ?? {};
    delete savedFilters[currentKey];
    StoreService.getInstance('session').set('SavedFilters', {
      ...savedFilters,
    });
  }
};

/**
 * PLP/SRP URL 여부를 확인한다.
 *
 * @param url
 * @returns PLP/SRP URL 여부
 */
export const isPlaceListPath = (url: string): boolean => {
  try {
    const path = new URL(url).pathname;
    const placeListPaths = values(EN_PLACELIST_TYPE).join('|');
    const placeListPathDivisionPaths = values(EN_PLACELIST_PATH_DIVISION)
      .map((key) => `${key}-.+`)
      .join('|');
    const placeListPathDivisionPathsWithoutRegion = values(EN_PLACELIST_PATH_DIVISION)
      .filter((key) => key !== EN_PLACELIST_PATH_DIVISION.REGION)
      .map((key) => `${key}-.+`)
      .join('|');
    const placeListURLRegexp = [
      `^\/(${placeListPaths})\/(r|a)-[^]+\/(${placeListPathDivisionPathsWithoutRegion})(\/$|$)`, // 국내숙소 PLP
      `^\/(${placeListPaths})\/(${placeListPathDivisionPaths})(\/$|$)`, // 국내숙소 PLP
      `^\/${EN_PLACELIST_TYPE.SEARCH}\/[^]+\/(r|a)-[^]+\/(${placeListPathDivisionPathsWithoutRegion})(\/$|$)`, // 국내숙소 PLP
      `^\/${EN_PLACELIST_TYPE.SEARCH}\/[^]+\/(r|a)-[^]+(\/$|$)`, // 국내숙소 PLP
      `^\/${EN_PLACELIST_TYPE.SEARCH}\/[^]+\/${EN_PLACELIST_PATH_DIVISION.KEYWORD}-[\\w]+(\/$|$)`, // 국내숙소 PLP
      `^\/${EN_PLACELIST_TYPE.SEARCH}\/[^]+(\/$|$)`,
    ];
    const findPath = placeListURLRegexp.find((regExp) => new RegExp(regExp).test(path));
    return !!findPath;
  } catch (e) {
    return false;
  }
};

/**
 * API에서 받은 퀵필터 정보를 화면 노출에 맞게 변환한다.
 *
 * @param quickFilters API에서 받은 퀵필터 정보
 */
export const transformQuickFilters = (quickFilters?: ISearchListPayload['quickFilters']): IFilterOptionItem[] => {
  return (
    quickFilters?.map((filter) => ({
      label: filter.title,
      value: filter.name,
      tooltip: filter.tooltip,
    })) ?? []
  );
};

/**
 * value가 `EN_PLACELIST_TYPE`인지 확인한다.
 *
 * @param value
 * @returns
 */
export const isPlaceListType = (value?: string): value is EN_PLACELIST_TYPE => {
  return Object.values(EN_PLACELIST_TYPE).includes(value as EN_PLACELIST_TYPE);
};

/**
 * 국내숙소 PLP/SRP 지면 정보를 반환한다. (API 호출 파라메터에 사용됨)
 *
 * @param placeListType
 * @returns
 */
export const getSearchDomesticPageName = (placeListType: EN_PLACELIST_TYPE): TSearchDomesticPageName => {
  const placeListTypeToPageName: { [key in EN_PLACELIST_TYPE]: TSearchDomesticPageName } = {
    [EN_PLACELIST_TYPE.MOTEL]: SEARCH_DOMESTIC_PAGE_NAME.MOTEL,
    [EN_PLACELIST_TYPE.AROUND_MOTEL]: SEARCH_DOMESTIC_PAGE_NAME.MOTEL,
    [EN_PLACELIST_TYPE.RECOMMEND]: SEARCH_DOMESTIC_PAGE_NAME.MOTEL,
    [EN_PLACELIST_TYPE.AROUND]: SEARCH_DOMESTIC_PAGE_NAME.AROUND,
    [EN_PLACELIST_TYPE.HOTEL]: SEARCH_DOMESTIC_PAGE_NAME.HOTEL,
    [EN_PLACELIST_TYPE.GUESTHOUSE]: SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE,
    [EN_PLACELIST_TYPE.PENSION]: SEARCH_DOMESTIC_PAGE_NAME.PENSION,
    [EN_PLACELIST_TYPE.KEYWORD]: SEARCH_DOMESTIC_PAGE_NAME.KEYWORD,
    [EN_PLACELIST_TYPE.AREA]: SEARCH_DOMESTIC_PAGE_NAME.AREA,
    [EN_PLACELIST_TYPE.SEARCH]: SEARCH_DOMESTIC_PAGE_NAME.KEYWORD,
  };
  return placeListTypeToPageName[placeListType];
};

/**
 * 국내숙소 PLP/SRP searchType 정보를 반환한다.
 *
 * @param pathDivision 상권 코드 or 숏컷탭
 * @param pathDivisionCuts 숏컷탭
 * @returns
 */
export const getSearchDomesticType = (
  pathDivision: string,
  pathDivisionCuts: string,
): TSearchDomesticType | undefined => {
  return Object.values(SEARCH_DOMESTIC_TYPE).find((value) => value === (pathDivisionCuts || pathDivision));
};

export const isRegionPageName = (pageName: TSearchDomesticPageName): boolean => {
  const regionPageName: string[] = [
    SEARCH_DOMESTIC_PAGE_NAME.MOTEL,
    SEARCH_DOMESTIC_PAGE_NAME.HOTEL,
    SEARCH_DOMESTIC_PAGE_NAME.PENSION,
    SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE,
  ];
  return regionPageName.includes(pageName);
};

/**
 * 국내숙소 PLP/SRP 지면 메타데이터를 반환한다.
 *
 * @param pageName
 * @param searchType
 * @returns
 */
export const getSearchDomesticMetaData = (
  pageName: TSearchDomesticPageName,
  searchType: TSearchDomesticType,
): EN_PAGE_NAME => {
  const pageNameToMetaData: { [key in TSearchDomesticPageName]: EN_PAGE_NAME } = {
    [SEARCH_DOMESTIC_PAGE_NAME.MOTEL]: EN_PAGE_NAME.PAGE__PLACELIST__MOTEL__SHORTCUT,
    [SEARCH_DOMESTIC_PAGE_NAME.HOTEL]: EN_PAGE_NAME.PAGE__PLACELIST__HOTEL__SHORTCUT,
    [SEARCH_DOMESTIC_PAGE_NAME.PENSION]: EN_PAGE_NAME.PAGE__PLACELIST__PENSION__SHORTCUT,
    [SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE]: EN_PAGE_NAME.PAGE__PLACELIST__GUESTHOUSE__SHORTCUT,
    [SEARCH_DOMESTIC_PAGE_NAME.KEYWORD]: EN_PAGE_NAME.PAGE__SEARCHLIST,
    [SEARCH_DOMESTIC_PAGE_NAME.AROUND]: getPlaceListAroundPageName(searchType),
    [SEARCH_DOMESTIC_PAGE_NAME.AREA]: getPlaceListAreaPageName(searchType),
  };
  return pageNameToMetaData[pageName];
};

export const getRegionTypeByPageName = (pageName: TSearchDomesticPageName): EN_REGION_TYPE => {
  const pageNameToRegionType: { [key in TSearchDomesticPageName]: EN_REGION_TYPE } = {
    [SEARCH_DOMESTIC_PAGE_NAME.MOTEL]: EN_REGION_TYPE.MOTEL,
    [SEARCH_DOMESTIC_PAGE_NAME.HOTEL]: EN_REGION_TYPE.HOTEL,
    [SEARCH_DOMESTIC_PAGE_NAME.PENSION]: EN_REGION_TYPE.PENSION,
    [SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE]: EN_REGION_TYPE.GUESTHOUSE,
    [SEARCH_DOMESTIC_PAGE_NAME.AREA]: EN_REGION_TYPE.MOTEL,
    [SEARCH_DOMESTIC_PAGE_NAME.AROUND]: EN_REGION_TYPE.MOTEL,
    [SEARCH_DOMESTIC_PAGE_NAME.KEYWORD]: EN_REGION_TYPE.MOTEL,
  };
  return pageNameToRegionType[pageName];
};

export const getDivisionKeyByPageName = (pageName: TSearchDomesticPageName): EN_PLACELIST_PATH_DIVISION => {
  const pageNameToDivisionKey: { [key in TSearchDomesticPageName]: EN_PLACELIST_PATH_DIVISION } = {
    [SEARCH_DOMESTIC_PAGE_NAME.MOTEL]: EN_PLACELIST_PATH_DIVISION.MKEY,
    [SEARCH_DOMESTIC_PAGE_NAME.HOTEL]: EN_PLACELIST_PATH_DIVISION.HKEY,
    [SEARCH_DOMESTIC_PAGE_NAME.PENSION]: EN_PLACELIST_PATH_DIVISION.PKEY,
    [SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE]: EN_PLACELIST_PATH_DIVISION.GKEY,
    [SEARCH_DOMESTIC_PAGE_NAME.AREA]: EN_PLACELIST_PATH_DIVISION.AREA,
    [SEARCH_DOMESTIC_PAGE_NAME.AROUND]: EN_PLACELIST_PATH_DIVISION.KEYWORD,
    [SEARCH_DOMESTIC_PAGE_NAME.KEYWORD]: EN_PLACELIST_PATH_DIVISION.KEYWORD,
  };
  return pageNameToDivisionKey[pageName];
};

/**
 * 클라이언트 로그에 전송 할 listName을 반환한다.
 *
 * @param pageName 페이지타입
 * @returns listName
 */
export const getListNameByPageNameForLog = (pageName: TSearchDomesticPageName): string => {
  const pageNameToListName: { [key in TSearchDomesticPageName]: string } = {
    [SEARCH_DOMESTIC_PAGE_NAME.MOTEL]: 'Motel',
    [SEARCH_DOMESTIC_PAGE_NAME.HOTEL]: 'Hotel',
    [SEARCH_DOMESTIC_PAGE_NAME.PENSION]: 'Pension',
    [SEARCH_DOMESTIC_PAGE_NAME.GUESTHOUSE]: 'GuestHouse',
    [SEARCH_DOMESTIC_PAGE_NAME.KEYWORD]: 'SearchResult',
    [SEARCH_DOMESTIC_PAGE_NAME.AROUND]: 'Around',
    [SEARCH_DOMESTIC_PAGE_NAME.AREA]: 'OldLocal',
  };
  return pageNameToListName[pageName];
};

/**
 * 클라이언트 로그에 전송 할 sourcePage 정보를 반환한다.
 */
export const getSorucePage = (): string | undefined => {
  try {
    if (checkIsServer()) {
      return;
    }
    const referrer = document.referrer;
    if (!referrer) {
      return;
    }
    const { pathname } = new URL(referrer);
    const pageMap = [
      { key: 'motel', value: 'Motel' },
      { key: 'hotel', value: 'Hotel' },
      { key: 'pension', value: 'Pension' },
      { key: 'guesthouse', value: 'GuestHouse' },
      { key: 'guestHouse', value: 'GuestHouse' },
    ];
    return pageMap.find((page) => pathname.includes(page.key))?.value;
  } catch (e) {
    return;
  }
};
